import { useEffect } from 'react';

const GUIDE_BACKGROUND = '#FDF6EC';
const POPUP_BOTTOM_MARGIN_RATIO = 0.3;
const POPUP_BOTTOM_MARGIN_FLOOR = 24;
const GUIDE_POPUP_SELECTOR = '[data-guide-popup="true"]';

const parsePixels = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getVisibleGuidePopups = () => Array.from(document.querySelectorAll(GUIDE_POPUP_SELECTOR))
  .filter((element) => {
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.visibility !== 'hidden' && style.display !== 'none' && rect.width > 0 && rect.height > 0;
  });

const getRequiredExtension = (baseDocumentHeight) => {
  const pageY = window.scrollY || window.pageYOffset || 0;

  return getVisibleGuidePopups().reduce((largestExtension, popup) => {
    const rect = popup.getBoundingClientRect();
    const popupBottom = pageY + rect.bottom;
    const margin = Math.max(POPUP_BOTTOM_MARGIN_FLOOR, rect.height * POPUP_BOTTOM_MARGIN_RATIO);
    return Math.max(largestExtension, Math.ceil(popupBottom + margin - baseDocumentHeight));
  }, 0);
};

const useGuideScrollExtension = (isActive, minimumExtraPixels = 0) => {
  useEffect(() => {
    if (!isActive || typeof window === 'undefined' || typeof document === 'undefined' || !document.body || !document.documentElement) {
      return undefined;
    }

    const body = document.body;
    const documentElement = document.documentElement;
    const previousBodyPaddingBottom = body.style.paddingBottom;
    const previousBodyBackgroundColor = body.style.backgroundColor;
    const previousDocumentBackgroundColor = documentElement.style.backgroundColor;
    const previousScrollPaddingBottom = documentElement.style.scrollPaddingBottom;
    const originalPaddingBottom = parsePixels(window.getComputedStyle(body).paddingBottom);
    const minimumExtension = Math.max(0, Number(minimumExtraPixels) || 0);
    let appliedExtension = 0;
    let frameId = null;

    const applyExtension = () => {
      frameId = null;
      const baseDocumentHeight = documentElement.scrollHeight - appliedExtension;
      const measuredExtension = Math.max(0, getRequiredExtension(baseDocumentHeight));
      appliedExtension = Math.max(minimumExtension, measuredExtension);

      body.style.paddingBottom = `${originalPaddingBottom + appliedExtension}px`;
      body.style.backgroundColor = GUIDE_BACKGROUND;
      documentElement.style.backgroundColor = GUIDE_BACKGROUND;
      documentElement.style.scrollPaddingBottom = `${appliedExtension}px`;
    };

    const scheduleApplyExtension = () => {
      if (frameId !== null) return;
      frameId = window.requestAnimationFrame(applyExtension);
    };

    scheduleApplyExtension();
    const settleTimer = window.setTimeout(scheduleApplyExtension, 100);
    window.addEventListener('resize', scheduleApplyExtension);
    window.addEventListener('scroll', scheduleApplyExtension, { passive: true });

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      window.clearTimeout(settleTimer);
      window.removeEventListener('resize', scheduleApplyExtension);
      window.removeEventListener('scroll', scheduleApplyExtension);
      body.style.paddingBottom = previousBodyPaddingBottom;
      body.style.backgroundColor = previousBodyBackgroundColor;
      documentElement.style.backgroundColor = previousDocumentBackgroundColor;
      documentElement.style.scrollPaddingBottom = previousScrollPaddingBottom;
    };
  }, [isActive, minimumExtraPixels]);
};

export default useGuideScrollExtension;
